import { useState, useEffect, useRef } from "react";
import { FaThumbsUp, FaThumbsDown, FaXmark, FaLink } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  getStoryGroups,
  getStoriesByGroup,
  postReactToStory,
  postVoteInStoryPoll,
} from "../../api/mainApi";

function News({ onUnviewedStatus, onClose }) {
  const { t } = useTranslation();
  const { groupId } = useParams();

  const [storyGroups, setStoryGroups] = useState({});
  const [userMeta, setUserMeta] = useState({
    totalGroups: 0,
    unviewedGroups: 0,
  });
  const [loading, setLoading] = useState(true);
  const [storiesData, setStoriesData] = useState({}); // groupId: { stories: [], loading: boolean }

  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [selectedStoryInGroupIndex, setSelectedStoryInGroupIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(0);
  const [transitionDuration, setTransitionDuration] = useState("1000ms");
  const timerRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const [selectedPollOptions, setSelectedPollOptions] = useState([]);

  // Compute groups from storyGroups
  const groups = Object.keys(storyGroups).map((groupId) => {
    const groupData = storyGroups[groupId];
    return {
      id: groupId,
      name: groupData.name,
      thumbnail: groupData.thumbnail,
      storiesIds: groupData.storiesIds,
      storiesCount: groupData.storiesIds.length,
      userInteraction: groupData.userInteraction,
    };
  });

  const selectedGroup = groups[selectedGroupIndex];
  const storiesInGroup = storiesData[selectedGroup?.id]?.stories || [];
  const selectedStory = storiesInGroup[selectedStoryInGroupIndex];
  const isDirectAccess = !!groupId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getStoryGroups();
        setStoryGroups(response.storyGroups);
        setUserMeta(response.userMeta);
      } catch (error) {
        // alert("Error fetching story groups: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (fullscreen) {
      setProgress(0);
      setIsPaused(false);
      setOverlayOpacity(0);
      startTimer();
      setTimeout(() => setOverlayOpacity(1), 500);
    }
    return () => clearInterval(timerRef.current);
  }, [fullscreen, selectedGroupIndex, selectedStoryInGroupIndex]);

  useEffect(() => {
    // Reset poll selection when story changes
    setSelectedPollOptions([]);
  }, [selectedGroupIndex, selectedStoryInGroupIndex]);

  useEffect(() => {
    // If we have groupId parameter, open the specific group in fullscreen
    if (groupId && groups.length > 0) {
      const groupIndex = groups.findIndex((group) => group.id === groupId);
      if (groupIndex !== -1 && !fullscreen) {
        openFullscreen(groupIndex);
      }
    }
  }, [groups, groupId, fullscreen]);

  const fetchStories = async (groupId) => {
    if (storiesData[groupId]) return;
    setStoriesData((prev) => ({
      ...prev,
      [groupId]: { stories: [], loading: true },
    }));
    try {
      const response = await getStoriesByGroup(groupId);
      const storiesArray = Object.keys(response.stories)
        .map((key) => ({
          id: key,
          ...response.stories[key],
        }))
        .sort((a, b) => a.groupPosition - b.groupPosition);
      setStoriesData((prev) => ({
        ...prev,
        [groupId]: { stories: storiesArray, loading: false },
      }));
      // Update group with first story thumbnail
      if (storiesArray.length > 0 && storiesArray[0].content?.thumbnail) {
        setStoryGroups((prevGroups) => ({
          ...prevGroups,
          [groupId]: {
            ...prevGroups[groupId],
            firstStoryThumbnail: storiesArray[0].content.thumbnail.url,
            firstStoryAlt: storiesArray[0].content.thumbnail.alt,
          },
        }));
      }
    } catch (error) {
      // alert("Error fetching stories: " + error.message);
      setStoriesData((prev) => ({
        ...prev,
        [groupId]: { stories: [], loading: false },
      }));
    }
  };

  const openFullscreen = async (groupIndex) => {
    const group = groups[groupIndex];
    await fetchStories(group.id);
    setSelectedGroupIndex(groupIndex);
    setSelectedStoryInGroupIndex(0);
    setFullscreen(true);
  };

  const closeFullscreen = () => {
    clearInterval(timerRef.current);
    setFullscreen(false);
    setProgress(0);
    setOverlayOpacity(0);
    setSelectedStoryInGroupIndex(0);
  };

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + 100 / (25 * 10);
        if (newProgress >= 100) {
          clearInterval(timerRef.current);
          nextStory();
          return 100;
        }
        return newProgress;
      });
    }, 100);
  };

  const pauseTimer = () => {
    setIsPaused(true);
    clearInterval(timerRef.current);
  };

  const resumeTimer = () => {
    if (isPaused) {
      setIsPaused(false);
      startTimer();
    }
  };

  const handlePointerDown = () => {
    clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      pauseTimer();
    }, 500);
  };

  const handlePointerUp = () => {
    clearTimeout(pauseTimeoutRef.current);
    resumeTimer();
  };

  const nextStory = () => {
    setTransitionDuration("0s");
    setOverlayOpacity(0);
    if (selectedStoryInGroupIndex < storiesInGroup.length - 1) {
      setSelectedStoryInGroupIndex(selectedStoryInGroupIndex + 1);
      setProgress(0);
      setTimeout(() => {
        setTransitionDuration("1000ms");
        setOverlayOpacity(1);
      }, 0);
    } else if (!isDirectAccess && selectedGroupIndex < groups.length - 1) {
      setSelectedGroupIndex(selectedGroupIndex + 1);
      setSelectedStoryInGroupIndex(0);
      setProgress(0);
      setTimeout(() => {
        setTransitionDuration("1000ms");
        setOverlayOpacity(1);
      }, 0);
    } else {
      closeFullscreen();
    }
  };

  const prevStory = () => {
    setTransitionDuration("0s");
    setOverlayOpacity(0);
    if (selectedStoryInGroupIndex > 0) {
      setSelectedStoryInGroupIndex(selectedStoryInGroupIndex - 1);
      setProgress(0);
      setTimeout(() => {
        setTransitionDuration("1000ms");
        setOverlayOpacity(1);
      }, 0);
    } else if (selectedGroupIndex > 0) {
      setSelectedGroupIndex(selectedGroupIndex - 1);
      setSelectedStoryInGroupIndex(0);
      setProgress(0);
      setTimeout(() => {
        setTransitionDuration("1000ms");
        setOverlayOpacity(1);
      }, 0);
    }
  };

  const handleReaction = async (reaction) => {
    try {
      // alert(`Posting ${reaction} for story ${selectedStory.id}`);
      await postReactToStory(
        selectedStory.id,
        selectedGroup.id,
        reaction,
      );
      // Update local state
      setStoriesData((prev) => {
        const newStoriesData = { ...prev };
        const stories = [...(newStoriesData[selectedGroup?.id]?.stories || [])];
        const storyIndex = stories.findIndex((s) => s.id === selectedStory.id);
        if (storyIndex !== -1) {
          stories[storyIndex] = {
            ...stories[storyIndex],
            userInteraction: {
              ...stories[storyIndex].userInteraction,
              reaction,
              reactionTimestamp: Date.now(),
            },
          };
          newStoriesData[selectedGroup?.id] = {
            ...newStoriesData[selectedGroup?.id],
            stories,
          };
        }
        return newStoriesData;
      });
    } catch (error) {
      // Handle error silently
    }
  };

  const submitVote = async (optionIds) => {
    try {
      // alert(`Voting for ${optionIds.join(", ")}`);
      await postVoteInStoryPoll(
        selectedStory.id,
        selectedGroup.id,
        optionIds,
      );
      // Update local state
      setStoriesData((prev) => {
        const newStoriesData = { ...prev };
        const stories = [...(newStoriesData[selectedGroup?.id]?.stories || [])];
        const storyIndex = stories.findIndex((s) => s.id === selectedStory.id);
        if (storyIndex !== -1) {
          stories[storyIndex] = {
            ...stories[storyIndex],
            userInteraction: {
              ...stories[storyIndex].userInteraction,
              pollVotes: optionIds,
              pollVotedAt: Date.now(),
            },
          };
          newStoriesData[selectedGroup?.id] = {
            ...newStoriesData[selectedGroup?.id],
            stories,
          };
        }
        return newStoriesData;
      });
      setSelectedPollOptions([]);
    } catch (error) {
      // alert("Error voting: " + error.message);
    }
  };

  const handlePollVote = (optionId) => {
    const isVoted = selectedStory?.userInteraction?.pollVotes?.length > 0;
    const isMultiple = selectedStory?.poll?.allowMultipleChoices;
    if (isVoted) return;
    if (!isMultiple) {
      // Single choice, submit immediately
      submitVote([optionId]);
    } else {
      // Multiple choice, toggle selection
      const isSelected = selectedPollOptions.includes(optionId);
      if (isSelected) {
        setSelectedPollOptions(
          selectedPollOptions.filter((id) => id !== optionId),
        );
      } else {
        setSelectedPollOptions([...selectedPollOptions, optionId]);
      }
    }
  };

  return (
    <div className="">
      {!isDirectAccess && (loading || groups.length > 0) && (
        <>
          {/* Circles container */}
          <div className="flex overflow-x-auto space-x-4">
            {loading
              ? // Skeleton loader
                Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    className="w-20 flex-shrink-0 flex flex-col items-center"
                  >
                    <div className="w-20 h-20 rounded-full bg-gray-700 animate-pulse" />
                    <div className="h-4 w-16 bg-gray-700 animate-pulse mt-2 rounded" />
                  </div>
                ))
              : groups.map((group, index) => {
                  const groupViewed = group.userInteraction.hasViewed;
                  return (
                    <div
                      key={group.id}
                      className="w-20 cursor-pointer transition-all flex-shrink-0 flex flex-col items-center"
                      onClick={() => openFullscreen(index)}
                    >
                      <div className="w-full h-20 relative">
                        {!groupViewed ? (
                          <div className="w-full h-full rounded-full p-1 bg-gradient-to-br from-emerald-500 to-cyan-500">
                            <div className="w-full h-full rounded-full overflow-hidden">
                              {group.thumbnail?.url ? (
                                <img
                                  src={group.thumbnail.url}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                                  <span className="text-white text-xs">
                                    {group.storiesCount}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-full rounded-full overflow-hidden border-2 border-gray-600">
                            {group.thumbnail?.url ? (
                              <img
                                src={group.thumbnail.url}
                                className="w-full h-full object-cover filter grayscale"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                                <span className="text-gray-400 text-xs">
                                  {group.storiesCount}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-center text-white mt-2 px-1 block truncate">
                        {group.name}
                      </p>
                    </div>
                  );
                })}
          </div>
        </>
      )}

      {/* Fullscreen Modal */}
      {fullscreen && selectedStory && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Progress bar */}
          <div className="absolute top-20 left-0 right-0 z-10 flex space-x-0.5 px-4 py-1">
            {storiesInGroup.map((_, i) => {
              let fillWidth = 0;
              if (i < selectedStoryInGroupIndex) fillWidth = 100;
              else if (i === selectedStoryInGroupIndex) fillWidth = progress;
              else fillWidth = 0;
              return (
                <div
                  key={i}
                  className="h-1 bg-gray-600 flex-1 rounded relative"
                >
                  <div
                    className="h-full bg-emerald-500 rounded absolute left-0 top-0 transition-all duration-100"
                    style={{ width: `${fillWidth}%` }}
                  ></div>
                </div>
              );
            })}
          </div>

          <div
            className="relative w-full h-full max-w-md max-h-dvh bg-cover bg-center"
            style={{
              backgroundImage: `url(${selectedStory?.content?.backgroundImage})`,
            }}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            {/* Date */}
            <p className="absolute top-24 left-4 text-gray-200 select-none text-sm z-10">
              {new Date(
                (selectedStory?.createdAt || 0) * 1000,
              ).toLocaleDateString("uk", { day: "numeric", month: "long" })}
            </p>

            {/* Close button */}
            <button
              className="absolute top-24 right-4 text-white text-2xl w-8 h-8 flex items-center justify-center z-10"
              onClick={closeFullscreen}
            >
              <FaXmark />
            </button>

            {/* Invisible navigation buttons */}
            {(selectedStoryInGroupIndex > 0 || selectedGroupIndex > 0) && (
              <div
                className="absolute left-0 top-0 w-40 h-full z-5 cursor-pointer opacity-0"
                onClick={(e) => {
                  e.stopPropagation();
                  prevStory();
                }}
              ></div>
            )}
            {(selectedStoryInGroupIndex < storiesInGroup.length - 1 ||
              (!isDirectAccess && selectedGroupIndex < groups.length - 1)) && (
              <div
                className="absolute right-0 top-0 w-40 h-full z-5 cursor-pointer opacity-0"
                onClick={(e) => {
                  e.stopPropagation();
                  nextStory();
                }}
              ></div>
            )}

            {/* Story content */}
            <div
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-2xl bg-gradient-to-t from-gray-950 to-transparent p-6 flex flex-col"
              style={{
                opacity: overlayOpacity,
                transition: `opacity ${transitionDuration}`,
              }}
            >
              {/* Title at top */}
              <div className="flex justify-between items-center mb-4">
                <p className="text-white text-xl font-medium text-left select-none">
                  {selectedStory?.content?.title}
                </p>
                {!selectedStory?.poll && (
                  <div className="flex space-x-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReaction("like");
                      }}
                      className={`flex items-center space-x-2 ${
                        selectedStory?.userInteraction?.reaction === "like"
                          ? "text-emerald-500"
                          : "text-white"
                      }`}
                    >
                      <FaThumbsUp size={20} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReaction("dislike");
                      }}
                      className={`flex items-center space-x-2 ${
                        selectedStory?.userInteraction?.reaction === "dislike"
                          ? "text-red-500"
                          : "text-white"
                      }`}
                    >
                      <FaThumbsDown size={20} />
                    </button>
                  </div>
                )}
              </div>

              {/* Below content */}
              <div className="flex-1 flex flex-col justify-end mb-4">
                {selectedStory?.poll ? (
                  <div className="flex flex-col space-y-4">
                    {selectedStory?.poll?.options?.map((option) => {
                      const isVoted =
                        selectedStory?.userInteraction?.pollVotes?.length > 0;
                      const isMultiple =
                        selectedStory?.poll?.allowMultipleChoices;
                      const votedSelected =
                        selectedStory?.userInteraction?.pollVotes?.includes(
                          option?.id,
                        );
                      const localSelected =
                        isMultiple && selectedPollOptions.includes(option?.id);
                      const isSelected = votedSelected || localSelected;
                      return (
                        <button
                          key={option?.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isVoted) handlePollVote(option?.id);
                          }}
                          className={`flex items-center py-3 px-4 rounded-lg border shadow-lg shadow-emerald-800/50 ${
                            isSelected
                              ? "bg-emerald-900 border-emerald-500"
                              : "bg-gray-800/90 border-gray-600"
                          } ${isVoted ? "opacity-75" : ""}`}
                          style={{ userSelect: "none" }}
                        >
                          <div
                            className={`flex-shrink-0 w-4 h-4 mr-3 rounded-full border ${
                              isSelected
                                ? "bg-emerald-500 border-emerald-500"
                                : "border-gray-600"
                            }`}
                          />
                          <span className="text-white text-left">
                            {option?.text}
                          </span>
                        </button>
                      );
                    })}
                    {selectedStory?.poll?.allowMultipleChoices &&
                      selectedPollOptions.length > 0 &&
                      !(
                        selectedStory?.userInteraction?.pollVotes?.length > 0
                      ) && (
                        <button
                          onClick={() => submitVote(selectedPollOptions)}
                          className="w-full py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                        >
                          Проголосувати
                        </button>
                      )}
                  </div>
                ) : (
                  <div className="text-left">
                    <div
                      className="text-white text-sm mb-4"
                      style={{ whiteSpace: "pre-wrap", userSelect: "none" }}
                      dangerouslySetInnerHTML={{
                        __html: selectedStory?.content?.text,
                      }}
                    />
                    {selectedStory?.content?.link && (
                      <a
                        href={selectedStory?.content?.link?.url}
                        className="text-emerald-700 py-1 px-4 bg-white rounded-full flex items-center justify-center gap-4 shadow-lg shadow-emerald-800"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ userSelect: "none" }}
                      >
                        <FaLink className="text-lg" />{" "}
                        {selectedStory?.content?.link?.text}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default News;
